import { Component, Host, h, Prop, Element, Event, EventEmitter, ComponentInterface, Method } from '@stencil/core'

@Component({
  tag: 'snap-action-sheet',
  styleUrl: 'snap-action-sheet.scss',
  shadow: true,
})
export class SnapActionSheet implements ComponentInterface {
  @Element() el: HTMLSnapActionSheetElement

  private wrapperEl: HTMLElement
  private backdropEl: HTMLElement
  private sheetGroup: HTMLElement
  private host: HTMLElement

  @Event({ eventName: 'snap-action-sheet-close', bubbles: true, composed: true }) snapActionSheetClose: EventEmitter

  @Prop({ reflect: true }) header: string = ''

  componentDidLoad() {
    this.swipeDown()
    this.open()
  }

  @Method()
  async open() {
    this.animate({
      duration: 400,
      timing: function (timeFraction) {
        return timeFraction
      },
      draw: progress => {
        this.wrapperEl.style.bottom = -100 + progress * 100 + '%'
      },
    })

    this.animate({
      duration: 400,
      timing: function (timeFraction) {
        return timeFraction
      },
      draw: progress => {
        this.backdropEl.style.opacity = progress * 100 + '%'
      },
    })
  }

  swipeDown() {
    let touchstartX = 0
    let touchendX = 0
    let touchstartY = 0
    let touchendY = 0
    const delta = 100

    this.sheetGroup.addEventListener('touchstart', function (e) {
      touchstartX = e.changedTouches[0].screenX
      touchstartY = e.changedTouches[0].screenY
    })

    this.sheetGroup.addEventListener('touchend', function (e) {
      touchendX = e.changedTouches[0].screenX
      touchendY = e.changedTouches[0].screenY
      handleGesure()
    })

    const handleGesure = () => {
      const xDiff = touchendX - touchstartX
      const yDiff = touchendY - touchstartY

      if (Math.abs(yDiff) > Math.abs(xDiff)) {
        if (yDiff > delta) {
          this.close()
        }
      }
    }
  }

  animate({ duration, draw, timing }) {
    const start = performance.now()

    requestAnimationFrame(function animate(time) {
      let timeFraction = (time - start) / duration
      if (timeFraction > 1) timeFraction = 1

      const progress = timing(timeFraction)

      draw(progress)

      if (timeFraction < 1) {
        requestAnimationFrame(animate)
      }
    })
  }

  @Method()
  async close() {
    this.animate({
      duration: 200,
      timing: function (timeFraction) {
        return timeFraction
      },
      draw: progress => {
        if (progress * -100 > 0) return
        this.wrapperEl.style.bottom = progress * -100 + '%'
      },
    })

    this.animate({
      duration: 200,
      timing: function (timeFraction) {
        return timeFraction
      },
      draw: progress => {
        this.backdropEl.style.opacity = 100 - progress * 100 + '%'
      },
    })

    setTimeout(() => {
      this.host.style.zIndex = 'auto'
      this.snapActionSheetClose.emit()
    }, 200)
  }

  backdropClick(e) {
    if (e.target.className === 'action-sheet-container') {
      this.close()
    }
  }

  render() {
    return (
      <Host role="dialog" aria-modal="true" tabindex="-1" ref={el => (this.host = el)}>
        <div tabindex="0"></div>

        <div class="action-sheet-backdrop" ref={el => (this.backdropEl = el)}></div>

        <div
          class="action-sheet-wrapper ion-overlay-wrapper"
          role="dialog"
          ref={el => (this.wrapperEl = el)}
          onClick={e => this.backdropClick(e)}>
          <div class="action-sheet-container">
            <div class="action-sheet-group" ref={el => (this.sheetGroup = el)}>
              <div class="draggable">
                <div class="action-sheet-move"></div>
              </div>
              <div class={`action-sheet-title ${!this.header ? ' no-title' : ''}`}>
                <div class="title-left">{this.header}</div>
                <div class="title-right">
                  <button
                    class="action-sheet-close-btn"
                    onClick={() => {
                      this.close()
                    }}>
                    <svg width="24" height="24" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
                      <path
                        fill-rule="evenodd"
                        clip-rule="evenodd"
                        d="M5.15164 5.1516C5.37667 4.92664 5.68184 4.80026 6.00004 4.80026C6.31823 4.80026 6.6234 4.92664 6.84844 5.1516L12 10.3032L17.1516 5.1516C17.2623 5.03699 17.3947 4.94557 17.5412 4.88268C17.6876 4.81979 17.845 4.78669 18.0044 4.7853C18.1637 4.78392 18.3217 4.81428 18.4692 4.87462C18.6167 4.93495 18.7506 5.02406 18.8633 5.13673C18.976 5.2494 19.0651 5.38338 19.1254 5.53086C19.1858 5.67833 19.2161 5.83635 19.2147 5.99568C19.2134 6.15502 19.1803 6.31248 19.1174 6.45889C19.0545 6.60529 18.963 6.7377 18.8484 6.8484L13.6968 12L18.8484 17.1516C19.067 17.3779 19.188 17.681 19.1852 17.9957C19.1825 18.3103 19.0563 18.6113 18.8338 18.8338C18.6113 19.0563 18.3104 19.1825 17.9957 19.1852C17.6811 19.1879 17.378 19.067 17.1516 18.8484L12 13.6968L6.84844 18.8484C6.62211 19.067 6.31899 19.1879 6.00436 19.1852C5.68972 19.1825 5.38874 19.0563 5.16625 18.8338C4.94376 18.6113 4.81756 18.3103 4.81483 17.9957C4.81209 17.681 4.93305 17.3779 5.15164 17.1516L10.3032 12L5.15164 6.8484C4.92667 6.62337 4.80029 6.3182 4.80029 6C4.80029 5.6818 4.92667 5.37663 5.15164 5.1516Z"
                        fill="#64748B"
                      />
                    </svg>
                  </button>
                </div>
              </div>
              <slot />
            </div>
          </div>
        </div>
      </Host>
    )
  }
}
